import { useReadContract, useReadContracts } from 'wagmi'
import { useAccount } from 'wagmi'
import { formatUnits } from 'viem'
import { BRIDGE_CONTRACT_ADDRESS, UNI2_CHAIN_ID } from '@/constants/addresses'
import { BRIDGE_ABI } from '@/constants/abis'

// Lock status enum from contract - MUST match contract exactly!
// Contract: enum LockStatus { PENDING, APPROVED, COMPLETED, CANCELLED, REJECTED }
export enum LockStatus {
  PENDING = 0,    // Locked, waiting for approval
  APPROVED = 1,   // Approved by admin, pending BSC transfer
  COMPLETED = 2,  // Completed - tokens sent to treasury
  CANCELLED = 3,  // Cancelled by user (before approval)
  REJECTED = 4,   // Rejected by admin, tokens returned
}

// Keep old name for backwards compatibility
export const LockState = LockStatus

export interface UserLock {
  lockId: bigint
  user: string
  amountIn: string
  amountOut: string
  bscRecipient: string
  status: LockStatus
  timestamp: Date
  externalTxHash: string
}

export function useUserBridges() {
  const { address } = useAccount()

  // Get user's lock IDs
  const { data: lockIds, isLoading: isLoadingIds, refetch: refetchIds } = useReadContract({
    address: BRIDGE_CONTRACT_ADDRESS,
    abi: BRIDGE_ABI,
    functionName: 'getUserLocks',
    args: address ? [address] : undefined,
    chainId: UNI2_CHAIN_ID,
    query: {
      enabled: !!address,
      staleTime: 0, // Always consider data stale
      gcTime: 0, // Don't cache
    },
  })

  // Build contracts array to fetch all locks in one call
  const lockContracts = (lockIds || []).map((lockId) => ({
    address: BRIDGE_CONTRACT_ADDRESS,
    abi: BRIDGE_ABI,
    functionName: 'getLock' as const,
    args: [lockId],
    chainId: UNI2_CHAIN_ID,
  }))

  // Fetch all lock details
  const { data: locksData, isLoading: isLoadingLocks, refetch: refetchLocks } = useReadContracts({
    contracts: lockContracts,
    query: {
      enabled: lockIds && lockIds.length > 0,
      staleTime: 0, // Always consider data stale
      gcTime: 0, // Don't cache
    },
  })

  // Parse the data into our UserLock interface
  const locks: UserLock[] = []

  if (lockIds && locksData) {
    for (let i = 0; i < lockIds.length; i++) {
      const lockId = lockIds[i]
      const lockResult = locksData[i]

      if (lockResult.status === 'success' && lockResult.result) {
        // getLock returns: [user, amountIn, amountOut, bscRecipient, timestamp, status, externalTxHash]
        const data = lockResult.result as readonly [string, bigint, bigint, string, bigint, number, `0x${string}`]

        locks.push({
          lockId,
          user: data[0],
          amountIn: formatUnits(data[1], 18),
          amountOut: formatUnits(data[2], 18),
          bscRecipient: data[3],
          timestamp: new Date(Number(data[4]) * 1000),
          status: Number(data[5]) as LockStatus,
          externalTxHash: data[6],
        })
      }
    }
  }

  // Sort by timestamp descending (newest first)
  locks.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

  const refetch = () => {
    refetchIds()
    refetchLocks()
  }

  return {
    locks,
    isLoading: isLoadingIds || isLoadingLocks,
    refetch,
  }
}
